import { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { OnChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  input,
  Input,
  output,
  signal,
  inject,
} from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// eslint-disable-next-line boundaries/element-types
import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
// eslint-disable-next-line boundaries/element-types
import { GdprAnalyticsService } from '@core/gdpr-data';
import { GdprService } from '@core/gdpr-logic';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  IconComponent,
  ButtonComponent,
} from '@core/user-interface-legacy';
import {
  RedactorComponent,
  RedactorInsertsDropdownComponent,
  type RedactorInsertsStep,
  type WysiwygLink,
  WysiwygLinkType,
} from '@core/wysiwyg-legacy';

@Component({
  selector: 'rt-redactor-inserts-button',
  templateUrl: './redactor-inserts-button.component.html',
  styleUrls: ['./redactor-inserts-button.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GdprAnalyticsService],
  imports: [
    ButtonComponent,
    OverlayOriginDirective,
    IconComponent,
    ConnectedOverlayDirective,
    RedactorInsertsDropdownComponent,
    TranslatePipe,
  ],
})
export class RedactorInsertsButtonComponent implements OnChanges {
  private readonly gdprAnalyticsService = inject(GdprAnalyticsService);
  private readonly gdprSvc = inject(GdprService);
  private readonly store = inject(Store);

  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly dropdownPositions = [
    new ConnectionPositionPair(
      { originX: 'end', originY: 'bottom' },
      { overlayX: 'end', overlayY: 'top' },
    ),
  ];

  @Input() public inserts: RedactorInsertsStep[];
  @Input() public input?: RedactorComponent;
  @Input() public emailTemplateInsideTemplate?: TemplateRef<any>;
  public disabled = input<boolean>(false);

  public saveAsTemplate = output<void>();
  public emailTemplateChoose = output<unknown>();

  public canManageCandidates: boolean = false;
  public filteredInserts = signal<RedactorInsertsStep[]>([]);

  private canScheduleEvents = false;
  private ngOnDestroy$ = new Subject<void>();
  private REDACTOR_REQUIRED_INSERTS: RedactorInsertsStep[] = [
    'questionnaires-templates',
    'schedules',
    'gdpr-links',
  ];

  public ngOnInit(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$))
      .subscribe(abilityMap => {
        this.canScheduleEvents = abilityMap[Ability.ScheduleEvents];
        this.canManageCandidates = abilityMap[Ability.ManageCandidates];
        this.setFilteredInserts();
      });
  }

  public ngOnChanges(): void {
    this.setFilteredInserts();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
  public openDropdown(event: MouseEvent, dropdownRef: ConnectedOverlayDirective): void {
    event.preventDefault();
    event.stopPropagation();
    dropdownRef.open = true;
    dropdownRef.updatePosition();
    this.cdRef.detectChanges();
  }

  public insertLink(link: WysiwygLink): void {
    if (!this.input) return;

    const linkType = link.type!;
    const isGdprLink = [
      WysiwygLinkType.GdprChange,
      WysiwygLinkType.GdprRemove,
      WysiwygLinkType.GdprConsentRequest,
    ].includes(linkType);
    if (isGdprLink) {
      const eventKind = <'correctionRequest' | 'removalRequest' | 'consentRequestLink'>{
        [WysiwygLinkType.GdprChange]: 'correctionRequest',
        [WysiwygLinkType.GdprRemove]: 'removalRequest',
        [WysiwygLinkType.GdprConsentRequest]: 'consentRequestLink',
      }[<WysiwygLinkType.GdprChange | WysiwygLinkType.GdprRemove | WysiwygLinkType.GdprConsentRequest>linkType];

      this.gdprAnalyticsService.trackInsertGDPRLink(eventKind);
    }

    this.input.restoreSelection();
    this.input.insertAtCaret(
      {
        ...link,
      },
      'link',
    );
  }

  public saveSelection(e: MouseEvent) {
    e.preventDefault();
    if (this.input) {
      this.input.saveSelection();
    }
  }

  private setFilteredInserts(): void {
    let filteredInserts = this.canScheduleEvents
      ? this.inserts
      : this.inserts.filter(i => i !== 'schedules');

    filteredInserts = this.input
      ? filteredInserts
      : filteredInserts.filter(insert => !this.REDACTOR_REQUIRED_INSERTS.includes(insert));

    if (!this.gdprSvc.gdprSettingEnabled || !this.canManageCandidates) {
      filteredInserts = filteredInserts.filter((i: RedactorInsertsStep) => i !== 'gdpr-links');
    }
    this.filteredInserts.set(filteredInserts);
  }
}
